<template>
  <v-list dense v-if="documents[0]">
    <template v-for="(document, i) in documents">
      <v-subheader v-if="document.header" :key="`${i}-header`">
        {{ document.header }}
      </v-subheader>

      <v-divider
        v-else-if="document.divider"
        :inset="document.inset"
        :key="`${i}-divider`"
      />

      <v-list-tile
        v-else
        :key="`${i}-title`"
        @click="
          newTab(typeof document.file === 'object' ? document.file.url : null)
        "
        target="blank"
      >
        <v-list-tile-avatar>
          <v-icon :color="document.color">{{ document.icon }}</v-icon>
        </v-list-tile-avatar>

        <v-list-tile-content
          v-if="typeof document.file === 'object'"
          class="black--text"
        >
          {{ document.title }}
        </v-list-tile-content>
        <v-list-tile-content
          v-else
          :key="`${i}-missing-file`"
          class="black--text"
        >
          Missing documents for {{ document.title }}
        </v-list-tile-content>

        <v-list-tile-action v-if="application && application.profile">
          <Flag icon v-if="document.flag" :value="document.flag" />
          <CreateFlag
            v-else-if="document.flagConfig"
            :target="document.flagConfig.target"
            :profile="application.profile"
            :filter="document.flagConfig.filter"
            @done="refresh"
          >
            <v-btn
              icon
              flat
              :color="themes.client.light.cancel"
              slot="activator"
            >
              <v-icon>mdi-flag</v-icon>
            </v-btn>
          </CreateFlag>
        </v-list-tile-action>
      </v-list-tile>
    </template>
  </v-list>
  <v-subheader v-else>No documents available</v-subheader>
</template>

<script>
// Components
import CreateFlag from "@/components/client/flags/CreateFlag";
import Flag from "@/components/client/flags/Flag";
import DocumentPreview from "@/components/client/DocumentPreview";

// Helpers
import _ from "lodash";
import { byEndsAt, byLevels } from "@/sorting/ProfileQualification";
import { byGradeValue } from "@/sorting/QualificationComponents";
import { mode } from "simple-statistics";

export default {
  props: { application: Object },

  components: { CreateFlag, DocumentPreview, Flag },

  mounted() {
    this.resetDocuments();
  },

  data() {
    return {
      documents: [],
    };
  },

  computed: {
    flags() {
      if (!_.get(this, "application.profile.flags[0]")) return [];

      return _.get(this, "application.profile.flags");
    },

    hasQualificationDocuments() {
      return this.qualifications.some((q) => {
        return _.get(q, "proofOfRegistration") || _.get(q, "transcript");
      });
    },

    hasQualifications() {
      return _.get(this, "qualifications[0]");
    },

    qualifications() {
      if (!_.get(this.application, "profile.profileQualifications")) return [];

      let qualifications = JSON.parse(
        JSON.stringify(this.application.profile.profileQualifications)
      );
      return qualifications
        .sort(byLevels({ country: this.levelsCountryMode(), asc: false }))
        .sort(byEndsAt({ asc: false }));
    },
  },

  methods: {
    levelsCountryMode() {
      let qualifications = JSON.parse(
        JSON.stringify(this.application.profile.profileQualifications)
      );
      const countries = qualifications
        .filter((pq) => _.get(pq, "qualification.levels[0]"))
        .reduce(
          (acc, { qualification: { levels } }) =>
            acc.concat(levels.map((l) => l.countryCode)),
          []
        );
      return countries[0] ? mode(countries) : null;
    },

    newTab(uri) {
      if (!uri) return;

      window.open(uri, "_blank");
    },
    refresh() {
      this.$store
        .dispatch("client/applications/fetch", { id: this.application.id })
        .then(this.resetDocuments);
    },
    resetDocuments() {
      const id = _.get(this.application, "profile.identityDocument");
      this.documents = [];

      if (id) {
        this.documents.push(
          { header: "Personal Documents" },
          {
            icon: "mdi-file-account",
            color: this.themes.client.light.primaryText,
            title: "Identification Document",
            file: id,
            flag: this.flags.find((f) =>
              RegExp("identity_document", "gi").test(f.key)
            ),
            flagConfig: {
              filter: ["identity_document"],
              target: { ...this.application.profile, type: "Profile" },
            },
          }
        );
      }

      if (this.hasQualifications)
        this.documents.push({ header: "Qualification Documents" });

      this.qualifications.forEach((q, i) => {
        let registration = _.get(q, "proofOfRegistration");
        let transcript = _.get(q, "transcript");

        if (transcript) {
          this.documents.push({
            icon: "mdi-file-document",
            color: this.themes.client.light.primaryText,
            title: `${q.qualificationTypeName} ${q.specialization}`,
            file: transcript,
            flag: this.flags.find(
              (f) =>
                f.targetType === "ProfileQualification" && f.targetId === q.id
            ),
            flagConfig: {
              filter: ["transcript"],
              target: { ...q, type: "ProfileQualification" },
            },
          });
        } else if (registration) {
          this.documents.push({
            icon: "mdi-file-document",
            color: this.themes.client.light.primaryText,
            title: `${q.qualificationTypeName} ${q.specialization}`,
            file: registration,
            flag: this.flags.find(
              (f) =>
                f.targetType === "ProfileQualification" && f.targetId === q.id
            ),
            flagConfig: {
              filter: ["proof_of_registration"],
              target: { ...q, type: "ProfileQualification" },
            },
          });
        } else {
          this.documents.push({
            icon: "mdi-close-circle",
            color: "warning",
            title: `${q.qualificationTypeName} ${q.specialization}`,
          });
        }
      });

      if (_.get(this, "application.profile.financial.incomes[0]")) {
        this.documents.push({
          header: `Household Income (${this.application.profile.financial.dependents} dependents)`,
        });
        this.application.profile.financial.incomes.forEach((i) => {
          if (i.proof) {
            this.documents.push({
              icon: "mdi-file-document",
              color: this.themes.client.light.primaryText,
              title: `${i.relationship}: ${i.typeOfProof}`,
              file: i.proof,
              flag: this.flags.find(
                (f) => f.targetType === "HouseholdIncome" && f.targetId === i.id
              ),
              flagConfig: {
                filter: ["household_income"],
                target: { ...i, type: "HouseholdIncome" },
              },
            });
          }
        });
      }
    },
  },
};
</script>
